import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Check } from 'lucide-react';
import { useTour } from '../../hooks/useTour';

const PADDING = 10;
const TOOLTIP_W = 340;
const TOOLTIP_H = 200;
const GAP = 16;

const getRect = (selector) => {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
};

/**
 * Compute a viewport-clamped tooltip position around the target rect.
 * Tries right -> left -> bottom -> top, then clamps inside viewport.
 */
const computeTooltipPos = (rect) => {
  if (!rect) {
    return { top: window.innerHeight / 2 - TOOLTIP_H / 2, left: window.innerWidth / 2 - TOOLTIP_W / 2, placement: 'center' };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const candidates = [
    // right
    { placement: 'right', top: rect.top + rect.height / 2 - TOOLTIP_H / 2, left: rect.right + GAP },
    // left
    { placement: 'left', top: rect.top + rect.height / 2 - TOOLTIP_H / 2, left: rect.left - TOOLTIP_W - GAP },
    // bottom
    { placement: 'bottom', top: rect.bottom + GAP, left: rect.left + rect.width / 2 - TOOLTIP_W / 2 },
    // top
    { placement: 'top', top: rect.top - TOOLTIP_H - GAP, left: rect.left + rect.width / 2 - TOOLTIP_W / 2 },
  ];

  for (const c of candidates) {
    if (c.left >= 8 && c.top >= 8 && c.left + TOOLTIP_W <= vw - 8 && c.top + TOOLTIP_H <= vh - 8) {
      return c;
    }
  }

  // Fallback: clamp the bottom placement
  const fallback = candidates[2];
  return {
    placement: fallback.placement,
    top: Math.min(Math.max(fallback.top, 8), vh - TOOLTIP_H - 8),
    left: Math.min(Math.max(fallback.left, 8), vw - TOOLTIP_W - 8),
  };
};

const SpotlightTour = ({ isOpen, steps, onClose, onStepChange }) => {
  const { currentStep, goNext, goPrev, skip } = useTour({ steps, isOpen, onClose });
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const rafRef = useRef(null);

  const activeStep = steps[currentStep];

  // Notify parent on step change (for mobile sidebar control etc.)
  useEffect(() => {
    if (!isOpen) return;
    onStepChange && onStepChange(currentStep, activeStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isOpen]);

  // Track active target rect with rAF, ResizeObserver, scroll, and resize
  useEffect(() => {
    if (!isOpen || !activeStep) return undefined;

    setReady(false);
    let cancelled = false;
    let observer = null;
    let targetEl = null;

    const update = () => {
      const r = getRect(activeStep.selector);
      if (r && !cancelled) {
        setRect(r);
        setReady(true);
      }
    };

    // Wait briefly for sidebar/animations to settle, then scroll into view + measure
    const start = () => {
      targetEl = document.querySelector(activeStep.selector);
      if (targetEl) {
        try {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        } catch (_) { /* ignore */ }
      }
      // Measure on next frames to capture post-scroll position
      let frames = 0;
      const tick = () => {
        update();
        frames += 1;
        if (frames < 30 && !cancelled) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      rafRef.current = requestAnimationFrame(tick);

      if (targetEl && 'ResizeObserver' in window) {
        observer = new ResizeObserver(update);
        observer.observe(targetEl);
      }
    };

    const t = setTimeout(start, 80);

    const onScroll = () => update();
    const onResize = () => update();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      clearTimeout(t);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, activeStep]);

  const handleOverlayClick = useCallback(() => {
    skip();
  }, [skip]);

  if (!isOpen || !activeStep) return null;

  // Cutout rect (with padding)
  const cutout = rect
    ? {
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }
    : null;

  const tooltipPos = rect ? computeTooltipPos(rect) : computeTooltipPos(null);
  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;

  const spring = { type: 'spring', stiffness: 220, damping: 28, mass: 0.7 };

  return (
    <AnimatePresence>
      <motion.div
        key="tour-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[55] pointer-events-none"
        data-testid="spotlight-tour"
      >
        {/* SVG overlay with cutout mask — handles dim + lets us animate the hole */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-auto"
          onClick={handleOverlayClick}
          aria-hidden="true"
        >
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {cutout && (
                <motion.rect
                  initial={false}
                  animate={{
                    x: cutout.left,
                    y: cutout.top,
                    width: cutout.width,
                    height: cutout.height,
                  }}
                  transition={spring}
                  rx={14}
                  ry={14}
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(2,6,23,0.72)"
            mask="url(#spotlight-mask)"
          />
        </svg>

        {/* Backdrop blur layers — 4 sides around the cutout (CSS backdrop-filter) */}
        {cutout && (
          <>
            <motion.div
              initial={false}
              animate={{ height: Math.max(0, cutout.top) }}
              transition={spring}
              className="absolute left-0 right-0 top-0 backdrop-blur-[6px] pointer-events-none"
              style={{ willChange: 'height' }}
            />
            <motion.div
              initial={false}
              animate={{
                top: cutout.top + cutout.height,
                height: Math.max(0, window.innerHeight - (cutout.top + cutout.height)),
              }}
              transition={spring}
              className="absolute left-0 right-0 backdrop-blur-[6px] pointer-events-none"
              style={{ willChange: 'top, height' }}
            />
            <motion.div
              initial={false}
              animate={{
                top: cutout.top,
                height: cutout.height,
                width: Math.max(0, cutout.left),
              }}
              transition={spring}
              className="absolute left-0 backdrop-blur-[6px] pointer-events-none"
              style={{ willChange: 'top, height, width' }}
            />
            <motion.div
              initial={false}
              animate={{
                top: cutout.top,
                left: cutout.left + cutout.width,
                height: cutout.height,
                width: Math.max(0, window.innerWidth - (cutout.left + cutout.width)),
              }}
              transition={spring}
              className="absolute backdrop-blur-[6px] pointer-events-none"
              style={{ willChange: 'top, left, height, width' }}
            />

            {/* Glow ring around the active element */}
            <motion.div
              initial={false}
              animate={{
                top: cutout.top,
                left: cutout.left,
                width: cutout.width,
                height: cutout.height,
                opacity: ready ? 1 : 0,
              }}
              transition={spring}
              className="absolute rounded-[14px] pointer-events-none"
              style={{
                boxShadow:
                  '0 0 0 1px rgba(251, 191, 36, 0.55), 0 0 0 6px rgba(251, 191, 36, 0.18), 0 0 40px rgba(251, 191, 36, 0.35)',
              }}
            >
              {/* Pulsing inner ring */}
              <motion.div
                className="absolute inset-0 rounded-[14px]"
                animate={{ opacity: [0.0, 0.5, 0.0], scale: [1, 1.04, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                style={{ boxShadow: '0 0 0 4px rgba(251, 191, 36, 0.25)' }}
              />
            </motion.div>
          </>
        )}

        {/* Floating tooltip card */}
        <motion.div
          initial={false}
          animate={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            opacity: ready ? 1 : 0,
          }}
          transition={spring}
          className="absolute pointer-events-auto"
          style={{ width: TOOLTIP_W }}
          onClick={(e) => e.stopPropagation()}
          data-testid="tour-tooltip"
        >
          <div className="relative rounded-2xl bg-white shadow-2xl shadow-black/30 border border-slate-200 overflow-hidden">
            {/* Top accent gradient */}
            <div className="h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300" />

            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold tracking-wider uppercase text-amber-600">
                    Step {currentStep + 1} / {steps.length}
                  </span>
                </div>
                <button
                  onClick={skip}
                  className="text-slate-400 hover:text-slate-700 transition-colors -mt-1 -mr-1 p-1 rounded-md hover:bg-slate-100"
                  aria-label="Skip tour"
                  data-testid="tour-skip-x"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22 }}
                >
                  <h3 className="text-lg font-bold text-slate-900 leading-tight" data-testid="tour-step-title">
                    {activeStep.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed" data-testid="tour-step-description">
                    {activeStep.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Progress dots */}
              <div className="mt-4 flex items-center gap-1.5">
                {steps.map((_, i) => (
                  <motion.span
                    key={i}
                    initial={false}
                    animate={{
                      width: i === currentStep ? 22 : 6,
                      backgroundColor: i === currentStep ? '#f59e0b' : i < currentStep ? '#fcd34d' : '#e2e8f0',
                    }}
                    transition={{ duration: 0.3 }}
                    className="h-1.5 rounded-full"
                  />
                ))}
              </div>

              {/* Actions */}
              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  onClick={skip}
                  className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
                  data-testid="tour-skip-btn"
                >
                  Skip tour
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={goPrev}
                    disabled={isFirst}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="tour-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={goNext}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors shadow-sm"
                    data-testid="tour-next-btn"
                  >
                    {isLast ? (
                      <>
                        Finish
                        <Check className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SpotlightTour;
